// Staff card on a workflow case: create / reset the patient's app login. The username is the case
// number; the password is shown ONCE (never stored in plaintext) so staff copy it to the patient.
import { useCallback, useEffect, useState } from 'react';
import type { PatientAccessResult, PatientAccessStatus } from '@alignova/shared';
import { ApiError } from '../../lib/api.ts';
import { patientApi } from '../../lib/patientApi.ts';

export function PatientAccessCard({ caseId }: { caseId: string }) {
  const [status, setStatus] = useState<PatientAccessStatus | null>(null);
  const [creds, setCreds] = useState<PatientAccessResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    patientApi.accessStatus(caseId).then(setStatus).catch(() => setStatus({ hasAccess: false, username: null }));
  }, [caseId]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<PatientAccessResult>) => {
    setBusy(true);
    setErr(null);
    setCopied(false);
    try {
      setCreds(await fn());
      load();
    } catch (e) {
      setErr(e instanceof ApiError && e.message ? e.message : 'Could not update patient access.');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!creds) return;
    void navigator.clipboard?.writeText(`Alignova patient app\nLogin: https://alignova.cloud/login\nUsername: ${creds.username}\nPassword: ${creds.password}`);
    setCopied(true);
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-level-1">
      <div className="mb-1 flex items-center gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted">Patient app access</div>
        {status && (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.hasAccess ? 'bg-status-progress-bg text-status-progress-fg' : 'bg-status-pending-bg text-status-pending-fg'}`}>
            {status.hasAccess ? `Active · ${status.username}` : 'No login yet'}
          </span>
        )}
      </div>
      <p className="mb-2 text-xs text-muted">The patient signs in at the normal login page and sees their trays, next change date and a daily wear timer.</p>

      {creds && (
        <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="mb-1 text-xs font-semibold text-amber-900">Share these with the patient - the password is shown only once.</div>
          <div className="font-mono">Username: <b>{creds.username}</b></div>
          <div className="font-mono">Password: <b>{creds.password}</b></div>
          <button onClick={copy} className="mt-2 rounded border border-amber-400 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100">
            {copied ? 'Copied ✓' : 'Copy login details'}
          </button>
        </div>
      )}
      {err && <p className="mb-2 text-xs text-status-urgent-fg">{err}</p>}

      <div className="flex flex-wrap gap-2">
        {!status?.hasAccess ? (
          <button onClick={() => void run(() => patientApi.grantAccess(caseId))} disabled={busy} className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-fg hover:opacity-90 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create patient login'}
          </button>
        ) : (
          <button onClick={() => void run(() => patientApi.resetAccess(caseId))} disabled={busy} className="rounded border border-line px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-2 disabled:opacity-50">
            {busy ? 'Resetting…' : 'Reset password'}
          </button>
        )}
      </div>
    </div>
  );
}
